import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RANK_NAMES, RANK_ORDER } from '@pk/shared';
import type { FrontierRegionView, HouseSummary, Id, TitleSummary } from '@pk/shared';
import { useTitle } from '../api/hooks';
import { Collapsible } from '../components/ui';
import { CharLink } from '../components/game';
import { IconChevron, IconCrown, IconScroll, IconSearch, IconX } from '../components/icons';
import { Heraldry } from '../components/Heraldry';
import { lawRows } from '../lib/laws';
import { FrontierRegions } from './Frontier';

interface Props {
  titles: TitleSummary[];
  kingdomIds: Id[];
  houses: HouseSummary[];
  myHouseId: Id | undefined;
  heldTitleIds: Id[];
  /** Identity colour per kingdom (matches the map). */
  kingdomColor: (id: Id) => string;
  /** Fill order: human share per kingdom (from the public world view). */
  fill?: { id: Id; humanHouses: number; houses: number }[];
  /** Frontier regions opened so far (world view). */
  frontier?: FrontierRegionView[];
  day?: number;
  onShow: (id: Id) => void;
  onClose: () => void;
}

const PinIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.4" />
  </svg>
);

/** The realm hierarchy, living inside the map: kingdoms, duchies, counties and holdings with "show on map" for each. */
export function RealmsPanel({ titles, kingdomIds, houses, myHouseId, heldTitleIds, kingdomColor, fill, frontier, day, onShow, onClose }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [toggled, setToggled] = useState<Set<Id>>(new Set());

  const { byId, children } = useMemo(() => {
    const byId = new Map<Id, TitleSummary>();
    const children = new Map<Id, TitleSummary[]>();
    for (const x of titles) byId.set(x.id, x);
    for (const x of titles) {
      if (!x.deJureParentId) continue;
      if (!children.has(x.deJureParentId)) children.set(x.deJureParentId, []);
      children.get(x.deJureParentId)!.push(x);
    }
    for (const list of children.values()) list.sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank] || a.name.localeCompare(b.name));
    return { byId, children };
  }, [titles]);

  const kingdomOf = (x: TitleSummary): Id | null => {
    let cur: TitleSummary | undefined = x;
    for (let g = 0; cur && g < 8; g++) {
      if (cur.rank === 'kingdom') return cur.id;
      cur = cur.deJureParentId ? byId.get(cur.deJureParentId) : undefined;
    }
    return null;
  };

  const mine = useMemo(
    () => heldTitleIds.map((id) => byId.get(id)).filter((x): x is TitleSummary => !!x).sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank] || a.name.localeCompare(b.name)),
    [heldTitleIds, byId],
  );
  const mineSet = useMemo(() => new Set(heldTitleIds), [heldTitleIds]);

  // The kingdom holding any of our titles comes first.
  const kingdoms = useMemo(() => {
    const list = kingdomIds.map((id) => byId.get(id)).filter((x): x is TitleSummary => !!x);
    const own = new Set<Id>();
    for (const x of mine) { const k = kingdomOf(x); if (k) own.add(k); }
    return list.sort((a, b) => Number(own.has(b.id)) - Number(own.has(a.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kingdomIds, byId, mine]);

  const q = query.trim().toLowerCase();
  // With a search: the matching titles plus every ancestor stay visible, and those branches open.
  const visible = useMemo(() => {
    if (!q) return null;
    const keep = new Set<Id>();
    for (const x of titles) {
      if (!x.name.toLowerCase().includes(q) && !(x.holderName ?? '').toLowerCase().includes(q)) continue;
      for (let cur: TitleSummary | undefined = x, g = 0; cur && g < 8; g++) { keep.add(cur.id); cur = cur.deJureParentId ? byId.get(cur.deJureParentId) : undefined; }
    }
    return keep;
  }, [q, titles, byId]);

  // Branches leading to our own titles start open; so do kingdoms and duchies.
  const initialOpen = useMemo(() => {
    const open = new Set<Id>();
    for (const x of titles) {
      if (x.rank === 'kingdom') open.add(x.id);
      if (mineSet.has(x.id)) for (let p = x.deJureParentId; p; p = byId.get(p)?.deJureParentId ?? null) open.add(p);
    }
    return open;
  }, [titles, mineSet, byId]);
  const isOpen = (id: Id) => (visible ? visible.has(id) : initialOpen.has(id) !== toggled.has(id));
  const toggle = (id: Id) => setToggled((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const shownKingdoms = visible ? kingdoms.filter((k) => visible.has(k.id)) : kingdoms;
  const shownMine = visible ? mine.filter((x) => visible.has(x.id)) : mine;

  return (
    <div className="rp">
      <div className="rp-head">
        <h3 className="rp-title"><IconCrown size={22} />{t('map.realms.title')}</h3>
        <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}><IconX size={20} /></button>
      </div>
      <label className="rp-search">
        <IconSearch size={18} />
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('map.realms.search')} aria-label={t('map.realms.search')} />
      </label>
      <div className="rp-scroll">
        {!q && frontier && frontier.length > 0 && <FrontierRegions regions={frontier} day={day ?? 0} onShow={onShow} />}
        {shownMine.length > 0 && (
          <section className="rp-sec rp-yours">
            <h4>{t('realm.yours')} <span className="count">{shownMine.length}</span></h4>
            <ul className="plain">
              {shownMine.map((x) => <Row key={x.id} node={x} mine onShow={onShow} showRank />)}
            </ul>
          </section>
        )}
        {shownKingdoms.length === 0 && <p className="muted small rp-empty">{q ? t('map.realms.noMatch') : t('realm.empty')}</p>}
        {shownKingdoms.map((k) => (
          <KingdomBlock key={k.id} kingdom={k} fill={fill?.find((f) => f.id === k.id)} color={kingdomColor(k.id)} house={houses.find((h) => h.id === k.holderHouseId)}
            children={children} visible={visible} isOpen={isOpen} toggle={toggle} myHouseId={myHouseId} onShow={onShow} />
        ))}
      </div>
    </div>
  );
}

function KingdomBlock({ kingdom, fill, color, house, children, visible, isOpen, toggle, myHouseId, onShow }: {
  kingdom: TitleSummary; fill?: { humanHouses: number; houses: number }; color: string; house: HouseSummary | undefined; children: Map<Id, TitleSummary[]>; visible: Set<Id> | null;
  isOpen: (id: Id) => boolean; toggle: (id: Id) => void; myHouseId: Id | undefined; onShow: (id: Id) => void;
}) {
  const { t } = useTranslation();
  const open = isOpen(kingdom.id);
  const realm = useTitle(open ? kingdom.id : null);
  const laws = realm.data?.laws;
  const kids = (children.get(kingdom.id) ?? []).filter((c) => !visible || visible.has(c.id));
  const mine = !!myHouseId && kingdom.holderHouseId === myHouseId;
  return (
    <section className="rp-kingdom" style={{ ['--kc' as string]: color }}>
      <div className={`rp-khead ${mine ? 'mine' : ''}`}>
        <button type="button" className="rp-ktoggle" onClick={() => toggle(kingdom.id)} aria-expanded={open}
          aria-label={t(open ? 'realm.collapse' : 'realm.expand', { name: kingdom.name })}>
          <IconChevron className={open ? 'rot' : ''} />
        </button>
        <span className="rp-banner" aria-hidden="true"><IconCrown size={18} /></span>
        <div className="rp-kname">
          <span className="rankname">{kingdom.name}</span>
          <small>
            {house && <span className="rp-crest" aria-hidden="true"><Heraldry seed={house.heraldrySeed} size={16} /></span>}
            {kingdom.holderId ? <CharLink id={kingdom.holderId} fallback={kingdom.holderName ?? undefined} /> : t('common.vacant')}
          </small>
          {fill && <small className="rp-fill">{t('map.realms.freeHouses', { human: fill.humanHouses, total: fill.houses })}</small>}
        </div>
        <Actions node={kingdom} onShow={onShow} />
      </div>
      {open && (
        <div className="rp-kbody">
          {kids.length > 0 && <ul className="tree">{kids.map((c) => <TreeNode key={c.id} node={c} children={children} visible={visible} isOpen={isOpen} toggle={toggle} myHouseId={myHouseId} onShow={onShow} />)}</ul>}
          {laws && !visible && (
            <Collapsible icon={<IconScroll />} title={t('realm.laws')} summary={t(`laws.succession_.${laws.succession}`)} className="flush">
              {lawRows(laws, t).map((r) => <div key={r.key} className="kv"><span>{r.label}</span><span>{r.value}</span></div>)}
              <p className="small muted" style={{ marginTop: 10, marginBottom: 0 }}>{t('realm.lawsNote')}</p>
              <Link className="btn small quiet" to={`/title/${kingdom.id}`} style={{ marginTop: 8 }}>{t('realm.lawsOpen')}<IconChevron size={18} /></Link>
            </Collapsible>
          )}
        </div>
      )}
    </section>
  );
}

function Actions({ node, onShow }: { node: TitleSummary; onShow: (id: Id) => void }) {
  const { t } = useTranslation();
  return (
    <span className="rp-actions">
      <button type="button" className="rp-act" onClick={() => onShow(node.id)} aria-label={`${t('map.realms.show')}: ${node.name}`} title={t('map.realms.show')}><PinIcon /></button>
      <Link className="rp-act" to={`/title/${node.id}`} aria-label={`${t('map.open')}: ${node.name}`} title={t('map.open')}><IconChevron size={22} /></Link>
    </span>
  );
}

function Row({ node, mine, onShow, showRank }: { node: TitleSummary; mine?: boolean; onShow: (id: Id) => void; showRank?: boolean }) {
  return (
    <li className={`rp-row ${mine ? 'mine' : ''}`}>
      <span className="swatch" style={{ background: node.color }} aria-hidden="true" />
      <button type="button" className="rp-name" onClick={() => onShow(node.id)}>
        <span>{node.name}</span>
        {showRank && <small>{RANK_NAMES[node.rank].title}</small>}
      </button>
      <Actions node={node} onShow={onShow} />
    </li>
  );
}

function TreeNode({ node, children, visible, isOpen, toggle, myHouseId, onShow }: {
  node: TitleSummary; children: Map<Id, TitleSummary[]>; visible: Set<Id> | null; isOpen: (id: Id) => boolean; toggle: (id: Id) => void;
  myHouseId: Id | undefined; onShow: (id: Id) => void;
}) {
  const { t } = useTranslation();
  const kids = (children.get(node.id) ?? []).filter((c) => !visible || visible.has(c.id));
  const open = isOpen(node.id);
  const mine = !!myHouseId && node.holderHouseId === myHouseId;
  return (
    <li>
      <div className={`node rp-node ${mine ? 'mine' : ''} r-${node.rank}`}>
        {kids.length > 0
          ? <button type="button" className="toggle" onClick={() => toggle(node.id)} aria-expanded={open} aria-label={t(open ? 'realm.collapse' : 'realm.expand', { name: node.name })}>{open ? '▾' : '▸'}</button>
          : <span className="toggle" aria-hidden="true" />}
        <span className="swatch" style={{ background: node.color }} aria-hidden="true" />
        <button type="button" className="nm rp-name" onClick={() => onShow(node.id)}>
          <span className={node.rank === 'duchy' ? 'rankname' : ''}>{node.name}</span>
          <small>{node.holderName ?? t('common.vacant')}{mine ? ` · ${t('common.you')}` : ''}</small>
        </button>
        <Actions node={node} onShow={onShow} />
      </div>
      {kids.length > 0 && open && (
        <ul className="tree">
          {kids.map((c) => <TreeNode key={c.id} node={c} children={children} visible={visible} isOpen={isOpen} toggle={toggle} myHouseId={myHouseId} onShow={onShow} />)}
        </ul>
      )}
    </li>
  );
}
